import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DataService } from '../../data.service';
import { BerService } from '../ber.service';
import { BER_FOMENUK, BerFomenu, BerMenupont, lathatoPont, menupontNyit } from '../ber-menu';
import { KeretService } from '../../keret.service';
import { honapNev } from '../ber.opciok';

/**
 *  A BÉR FŐMENÜJE – a modul kezdőlapja ügyfélválasztás után, a NAPLO főmenüjének párja
 *  (naplo/fomenu): felül a főmenük nagy gombjai, alattuk a kiválasztott főmenü pontjai
 *  csempékként. Ugyanazt a menüt mutatja, mint a fejléc lenyílója (ber-menu.ts), a jogok és
 *  az ügyfél adózási formája szerint szűrve. Ügyfél nélkül az ügyfélválasztóra visz.
 */
@Component({
  selector: 'app-ber-fomenu',
  templateUrl: './fomenu.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss', '../../naplo/fomenu/fomenu.component.scss', './fomenu.component.scss'],
})
export class BerFomenuComponent implements OnInit, OnDestroy {
  private jogos: { f: BerFomenu; pontok: BerMenupont[] }[] = [];
  valasztott: string | null = null;
  honapNev = honapNev;
  private sub?: Subscription;
  private static readonly TAR = 'ber-fomenu-utolso';
  readonly modBillNev = /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent) ? '⌥ Option' : 'Alt';

  constructor(private ber: BerService, private data: DataService, private router: Router, private keret: KeretService) {}

  get aktiv() { return this.ber.aktiv; }
  /** A látható főmenük: jog szerint (egyszer számolva) és az ügyfél szerint (élőben). */
  /*  Tárolva (2026-11-05): a getter minden változásészleléskor új menülistát adott, az *ngFor ezért a főmenüt újra
   *  és újra felépítette. A jog szerinti lista és az aktív ügyfél adatai a kulcs. */
  private menukTar: { j: any; a: string; v: any[] } | null = null;
  get menuk() {
    const a = JSON.stringify(this.aktiv || {});
    if (this.menukTar && this.menukTar.j === this.jogos && this.menukTar.a === a) return this.menukTar.v;
    const v = this.jogos.map((x) => ({ f: x.f, pontok: x.pontok.filter((p) => lathatoPont(p, this.aktiv)) })).filter((x) => x.pontok.length);
    this.menukTar = { j: this.jogos, a, v };
    return v;
  }
  get pontok(): BerMenupont[] { return this.menuk.find((x) => x.f.kulcs === this.valasztott)?.pontok || []; }

  /*  TEENDŐK – a megbízottak havi biztosítási elbírálásából (ber-megbizas.js): ahol a hónapban
   *  biztosított lett, a 08E (T1041) bejelentés; ahol bejelentették, de már nem biztosított, a törlés. */
  teendok: any[] = [];
  async teendoBetolt() {
    if (!this.aktiv.ugyfelId) { this.teendok = []; return; }
    try { this.teendok = await this.ber.get('/megbizas-biztositas/' + this.aktiv.ugyfelId); } catch { this.teendok = []; }
  }
  lejart(t: any) { return t.allapot === 'teendo' && t.hatarido && t.hatarido < new Date().toISOString().slice(0, 10); }
  ft(x: any) { return Math.round(Number(x) || 0).toLocaleString('hu-HU'); }
  /** „novemberben”, „januárban” – a hónapnév hangrendje szerint. */
  honapban(ho: number) { const n = honapNev(ho).toLowerCase(); return n + (/(szeptember|október|november|december)$/.test(n) ? 'ben' : 'ban'); }
  tolIg(t: any) {
    const f = (d: string) => String(d || '').slice(5).replace('-', '.') + '.';
    return `${f(t.tol)} – ${f(t.ig)}`;
  }
  /** A hónap 08E-je (T1041) a NAV-nyomtatványok között: ha még nincs, a bérből kitöltve létrejön. */
  nyit08E(t: any) { this.router.navigate(['/ber/bevallasok'], { queryParams: { kod: 'T1041', ev: t.ev, ho: t.ho, gyakorisag: 'havi' } }); }
  async kesz(t: any) {
    try { await this.ber.put(`/megbizas-biztositas/${this.aktiv.ugyfelId}/${t.id}`, { allapot: 'beadva' }); await this.teendoBetolt(); }
    catch { /* a lista marad */ }
  }

  async ngOnInit() {
    const a = this.ber.aktiv.ugyfelId ? this.ber.aktiv : await this.ber.init();
    if (!a.ugyfelId) { this.router.navigateByUrl('/ber/ugyfelek', { replaceUrl: true }); return; }
    this.teendoBetolt();
    //  A csempék AZONNAL jelenjenek meg és legyenek kattinthatók – NE a jog-lekérésre várjanak
    //  (különben indítás után, amíg a hozzaferesek() az auth-ra vár, a sárga menü „halott”/kattinthatatlan).
    //  A teljes listát mutatjuk, a jogszűrés utólag finomít; a hozzáférést a kiszolgáló amúgy is eldönti.
    this.jogos = BER_FOMENUK.map((f) => ({ f, pontok: f.pontok.slice() }));
    let tarolt: string | null = null;
    try { tarolt = localStorage.getItem(BerFomenuComponent.TAR); } catch { /* privát mód */ }
    this.valasztott = this.menuk.some((x) => x.f.kulcs === tarolt) ? tarolt : (this.menuk[0]?.f.kulcs || null);
    //  A jogok megérkezése után szűkítjük a pontokat, és újraérvényesítjük a kiválasztott főmenüt.
    try {
      const szabad = (await this.data.hozzaferesek()).szabad;
      this.jogos = BER_FOMENUK.map((f) => ({ f, pontok: f.pontok.filter((p) => szabad(p.jog)) }));
      if (this.valasztott && !this.menuk.some((x) => x.f.kulcs === this.valasztott)) this.valasztott = this.menuk[0]?.f.kulcs || null;
    } catch { /* a kiszolgáló úgyis dönt – marad a teljes lista */ }
    // Ügyfélváltáskor (pl. az adózási forma miatt eltűnő menüpont) a kiválasztott főmenü maradjon érvényes.
    this.sub = this.ber.aktiv$.subscribe(() => {
      this.teendoBetolt();
      if (this.valasztott && !this.menuk.some((x) => x.f.kulcs === this.valasztott)) this.valasztott = this.menuk[0]?.f.kulcs || null;
    });
  }
  ngOnDestroy() { this.sub?.unsubscribe(); }

  valaszt(k: string) {
    this.valasztott = k;
    try { localStorage.setItem(BerFomenuComponent.TAR, k); } catch { /* nem kritikus */ }
  }
  nyit(p: BerMenupont) { menupontNyit(p, this.router, this.keret); }
}
